import React, { useState, useEffect, useMemo } from 'react';
import { 
  Box, 
  Typography, 
  CircularProgress, 
  Button, 
  Paper,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
  ToggleButton,
  ToggleButtonGroup
} from '@mui/material';
import ChatIcon from '@mui/icons-material/Chat';
import VisibilityIcon from '@mui/icons-material/Visibility';
import CloseIcon from '@mui/icons-material/Close';
import clientsApi from '../../api/clientsApi';
import PaginatedTable from '../../components/PaginatedTable';
import SortableTableHeader from '../../components/SortableTableHeader';

const parseCommunicationMessage = (message = '') => {
  if (!message) {
    return { html: '', text: '' };
  }

  try {
    if (typeof window === 'undefined' || typeof window.DOMParser === 'undefined') {
      throw new Error('DOMParser unavailable');
    }

    const parser = new window.DOMParser();
    const doc = parser.parseFromString(message, 'text/html');

    doc.querySelectorAll('script, style, iframe, object, embed').forEach((node) => node.remove());

    const cleanHtml = doc.body.innerHTML;
    const text = doc.body.textContent
      ?.replace(/\u00a0/g, ' ')
      .replace(/\r?\n/g, '\n')
      .replace(/\n{3,}/g, '\n\n')
      .replace(/\n[\t ]+/g, '\n')
      .trim() || '';

    return { html: cleanHtml, text };
  } catch (error) {
    console.warn('Unable to parse communication message', error);
    const fallbackText = message
      .replace(/<br\s*\/?>/gi, '\n')
      .replace(/<[^>]*>/g, '')
      .replace(/\u00a0/g, ' ')
      .replace(/\r?\n/g, '\n')
      .replace(/\n{3,}/g, '\n\n')
      .trim();

    return { html: message, text: fallbackText };
  }
};

const ClientCommunications = ({ clientId, client }) => {
  // Client data is passed as prop, no need for state
  const [communications, setCommunications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedCommunication, setSelectedCommunication] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(50);
  const [totalCommunications, setTotalCommunications] = useState(0);
  const [sortField, setSortField] = useState('sent_date');
  const [sortDirection, setSortDirection] = useState('desc');
  const [messageViewMode, setMessageViewMode] = useState('rich');

  const fetchData = async () => {
    setLoading(true);
    try {
      // Fetch communications data (client data is passed as prop)
      const communicationsData = await clientsApi.getClientCommunications(clientId, {
        skip: page * pageSize,
        limit: pageSize,
        order_by: sortField,
        order_direction: sortDirection
      });
      setCommunications(communicationsData.communications || []);
      setTotalCommunications(communicationsData.total || 0);
      setError(null);
    } catch (err) {
      console.error('Error fetching client communications:', err);
      setError('Failed to load client communications. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (clientId) {
      fetchData();
    }
  }, [clientId, page, pageSize, sortField, sortDirection]);

  const handlePageChange = (newPage) => {
    setPage(newPage);
  };

  const handlePageSizeChange = (newPageSize) => {
    setPageSize(newPageSize);
    setPage(0); // Reset to first page when changing page size
  };

  const handleSort = (field, direction) => {
    setSortField(field);
    setSortDirection(direction);
    setPage(0); // Reset to first page when sorting changes
  };

  const handleBack = () => {
    // This component is embedded in the client detail page
  };

  const handleViewCommunication = (communication) => {
    setSelectedCommunication(communication);
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setSelectedCommunication(null);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Not provided';
    return new Date(dateString).toLocaleDateString();
  };

  const getTypeColor = (type) => {
    switch (type?.toLowerCase()) {
      case 'email':
        return 'primary';
      case 'sms':
      case 'text':
        return 'success';
      case 'phone':
      case 'call':
        return 'warning';
      case 'letter':
      case 'mail':
        return 'info';
      default:
        return 'default';
    }
  };

  const messageContent = useMemo(() => {
    if (!selectedCommunication?.message) {
      return { html: '', text: '' };
    }
    return parseCommunicationMessage(selectedCommunication.message);
  }, [selectedCommunication?.message]);

  const hasRichContent = useMemo(() => {
    const html = messageContent.html?.trim();
    const text = messageContent.text?.trim();

    if (!html) {
      return false;
    }

    if (!text) {
      return html.length > 0;
    }

    return html !== text;
  }, [messageContent.html, messageContent.text]);

  const handleMessageViewModeChange = (_, nextView) => {
    if (nextView !== null) {
      setMessageViewMode(nextView);
    }
  };

  useEffect(() => {
    if (!modalOpen) {
      return;
    }

    setMessageViewMode(hasRichContent ? 'rich' : 'plain');
  }, [modalOpen, hasRichContent]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '80vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  return (
    <Box>

      <PaginatedTable
        title={`Communications for ${client?.first_name} ${client?.last_name}`}
        icon={<ChatIcon color="primary" sx={{ mr: 1 }} />}
        total={totalCommunications}
        page={page}
        pageSize={pageSize}
        onPageChange={handlePageChange}
        onPageSizeChange={handlePageSizeChange}
        loading={loading}
      >
        {communications.length === 0 ? (
          <Alert severity="info">No communications found for this client.</Alert>
        ) : (
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <SortableTableHeader
                    field="subject"
                    label="Subject"
                    sortField={sortField}
                    sortDirection={sortDirection}
                    onSort={handleSort}
                  />
                  <SortableTableHeader
                    field="type"
                    label="Type"
                    sortField={sortField}
                    sortDirection={sortDirection}
                    onSort={handleSort}
                  />
                  <SortableTableHeader
                    field="recipient"
                    label="Recipient"
                    sortField={sortField}
                    sortDirection={sortDirection}
                    onSort={handleSort}
                  />
                  <SortableTableHeader
                    field="sender"
                    label="Sender"
                    sortField={sortField}
                    sortDirection={sortDirection}
                    onSort={handleSort}
                  />
                  <SortableTableHeader
                    field="sent_date"
                    label="Sent Date"
                    sortField={sortField}
                    sortDirection={sortDirection}
                    onSort={handleSort}
                  />
                  <TableCell>Message Preview</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {communications.map((communication) => (
                  <TableRow key={communication.id} hover>
                    <TableCell>
                      <Typography variant="body2" fontWeight="medium">
                        {communication.subject || 'No Subject'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip 
                        label={communication.type || 'Unknown'} 
                        color={getTypeColor(communication.type)}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {communication.recipient || 'Unknown'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {communication.sender || 'Unknown'}
                      </Typography>
                    </TableCell>
                    <TableCell>{formatDate(communication.sent_date)}</TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ maxWidth: 200 }}>
                        {communication.message 
                          ? communication.message.substring(0, 100) + (communication.message.length > 100 ? '...' : '')
                          : 'No message'
                        }
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Tooltip title="View Details">
                        <IconButton 
                          size="small"
                          onClick={() => handleViewCommunication(communication)}
                        >
                          <VisibilityIcon />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </PaginatedTable>

      <Dialog open={modalOpen} onClose={handleCloseModal} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="h6">Communication Details</Typography>
            <IconButton onClick={handleCloseModal} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          {selectedCommunication && (
            <Box sx={{ pt: 1 }}>
              <Box sx={{ mb: 3 }}>
                <Typography variant="h6" gutterBottom>
                  {selectedCommunication.subject || 'No Subject'}
                </Typography>
                <Chip 
                  label={selectedCommunication.type || 'Unknown'} 
                  color={getTypeColor(selectedCommunication.type)}
                  size="small"
                  sx={{ mb: 2 }}
                />
              </Box>
              
              <Divider sx={{ mb: 2 }} />
              
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  Communication Details
                </Typography>
                <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2, mb: 2 }}>
                  <Box>
                    <Typography variant="body2" color="text.secondary">From:</Typography>
                    <Typography variant="body1">{selectedCommunication.sender || 'Unknown'}</Typography>
                  </Box>
                  <Box>
                    <Typography variant="body2" color="text.secondary">To:</Typography>
                    <Typography variant="body1">{selectedCommunication.recipient || 'Unknown'}</Typography>
                  </Box>
                  <Box>
                    <Typography variant="body2" color="text.secondary">Sent Date:</Typography>
                    <Typography variant="body1">{formatDate(selectedCommunication.sent_date)}</Typography>
                  </Box>
                  <Box>
                    <Typography variant="body2" color="text.secondary">Type:</Typography>
                    <Typography variant="body1">{selectedCommunication.type || 'Unknown'}</Typography>
                  </Box>
                </Box>
              </Box>
              
              {selectedCommunication.message && (
                <>
                  <Divider sx={{ mb: 2 }} />
                  <Box>
                    <Box
                      sx={{
                        display: 'flex',
                        flexDirection: { xs: 'column', sm: 'row' },
                        alignItems: { xs: 'flex-start', sm: 'center' },
                        justifyContent: 'space-between',
                        gap: 1,
                        mb: 1
                      }}
                    >
                      <Typography variant="subtitle2" color="text.secondary">
                        Message Content
                      </Typography>
                      {hasRichContent && (
                        <ToggleButtonGroup
                          value={messageViewMode}
                          exclusive
                          size="small"
                          onChange={handleMessageViewModeChange}
                          sx={{ backgroundColor: 'background.paper', borderRadius: 999 }}
                        >
                          <ToggleButton value="rich">Formatted</ToggleButton>
                          <ToggleButton value="plain">Plain Text</ToggleButton>
                        </ToggleButtonGroup>
                      )}
                    </Box>
                    <Paper
                      variant="outlined"
                      sx={{
                        p: 2.5,
                        backgroundColor: 'grey.50',
                        maxHeight: 320,
                        overflow: 'auto'
                      }}
                    >
                      {messageViewMode === 'rich' && hasRichContent ? (
                        <Box
                          sx={{
                            fontSize: '0.95rem',
                            lineHeight: 1.6,
                            wordBreak: 'break-word',
                            '& p': { mb: 2 },
                            '& ul, & ol': { pl: 3, mb: 2 },
                            '& li': { mb: 1 },
                            '& a': { color: 'primary.main', textDecoration: 'underline', fontWeight: 500 },
                            '& img': { maxWidth: '100%', borderRadius: 2, my: 2, boxShadow: '0 8px 24px rgba(15, 23, 42, 0.1)' },
                            '& h1, & h2, & h3, & h4': { mt: 2.5, mb: 1.5, fontSize: '1.05rem' }
                          }}
                          dangerouslySetInnerHTML={{ __html: messageContent.html }}
                        />
                      ) : (
                        <Typography variant="body1" sx={{ whiteSpace: 'pre-line', lineHeight: 1.6 }}>
                          {messageContent.text || selectedCommunication.message}
                        </Typography>
                      )}
                    </Paper>
                  </Box>
                </>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseModal} variant="contained">
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ClientCommunications; 
